/**
 * Registry Explorer Page (DRR-111)
 *
 * Unified view of the config type registry for architects.
 * 4 tabs: Config Types | Dimensions | Filename Abbreviations | Filename Decoder
 *
 * Data source: backend GraphQL queries backed by compile-time embedded
 * config-type-registry via global_registry().
 *
 * Replaces hardcoded CONFIG_TYPES in DevDashboardPage.
 *
 * Pattern: follows WorkflowRegistryPage (stats cards + searchable table + detail drawer)
 */

import { useMemo, useState, useCallback } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@apollo/client/react';
import type { DocumentNode } from '@apollo/client';

// ============================================================================
// GraphQL Queries
// ============================================================================

// DECODE_CONFIG_FILENAME: canonical query from useDecodeConfigFilename hook
import { DECODE_CONFIG_FILENAME } from '@/hooks/useDecodeConfigFilename';

import {
  useGetRegistryOverviewQuery,
  useGetConfigTypeRegistryQuery,
  useGetDimensionCodeTablesQuery,
  useGetFilenameAbbreviationTablesQuery,
  GetConfigTypeRegistryDocument,
  GetRegistryOverviewDocument,
  GetDimensionCodeTablesDocument,
  GetFilenameAbbreviationTablesDocument,
} from '@/generated/graphql';
import {
  IconDatabase,
  IconSearch,
  IconAlertTriangle,
  IconBinaryTree,
  IconCode,
  IconFileCode,
  IconHash,
  IconListDetails,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  SimpleGrid,
  Loader,
  Tabs,
  TextInput,
  Drawer,
  Code,
  Divider,
  List,
  Tooltip,
} from '@mantine/core';
// ============================================================================
// Types
// ============================================================================

interface ConfigType {
  name: string;
  symbol: string;
  filenamePrefix: string;
  skVerbose: string;
  skCompressed: string;
  filenameTemplate: string | null;
  filenamePattern: string;
  idField: string;
  sourceDirs: string[];
  legacySk: boolean;
  seedLoader: string | null;
  dimensions: {
    name: string;
    mapsTo: string | null;
    source: string | null;
    storage: string | null;
    defaultValue: string | null;
    examples: string[];
  }[];
}

interface DimensionTable {
  name: string;
  count: number;
  codes: { value: string; code: string }[];
}

interface RegistryOverviewData {
  registryOverview: { registryVersion: string; configTypeCount: number; dimensionTableCount: number; totalDimensionCodes: number };
}

interface ConfigTypeRegistryData {
  configTypeRegistry: ConfigType[];
}

interface DimensionTablesData {
  dimensionCodeTables?: DimensionTable[];
  filenameAbbreviationTables?: DimensionTable[];
}

interface DecodeFilenameData {
  decodeConfigFilename: {
    filename: string;
    valid: boolean;
    configType: string | null;
    prefix: string | null;
    symbol: string | null;
    segments: { dimension: string; rawCode: string; decodedValue: string | null }[];
    error: string | null;
  };
}

// ============================================================================
// Components
// ============================================================================

function StatsCards({ overview }: { overview: { registryVersion: string; configTypeCount: number; dimensionTableCount: number; totalDimensionCodes: number } }) {
  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="violet"><IconDatabase size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Registry</Text>
        </Group>
        <Text size="lg" fw={700}>v{overview.registryVersion}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconListDetails size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Config Types</Text>
        </Group>
        <Text size="lg" fw={700}>{overview.configTypeCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="teal"><IconBinaryTree size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Dimension Tables</Text>
        </Group>
        <Text size="lg" fw={700}>{overview.dimensionTableCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="orange"><IconHash size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Total Codes</Text>
        </Group>
        <Text size="lg" fw={700}>{overview.totalDimensionCodes}</Text>
      </Card>
    </SimpleGrid>
  );
}

function ConfigTypesTab() {
  const { data, loading, error } = useQuery<ConfigTypeRegistryData>(GetConfigTypeRegistryDocument);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ConfigType | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const types: ConfigType[] = useMemo(() => data?.configTypeRegistry ?? [], [data]);

  const filtered = useMemo(() => {
    if (!search) return types;
    const q = search.toLowerCase();
    return types.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.symbol.toLowerCase().includes(q) ||
        t.filenamePrefix.toLowerCase().includes(q)
    );
  }, [types, search]);

  const handleRowClick = useCallback(
    (ct: ConfigType) => {
      setSelected(ct);
      openDrawer();
    },
    [openDrawer]
  );

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red" icon={<IconAlertTriangle size={16} />}>{error.message}</Alert>;

  return (
    <>
      <TextInput
        placeholder="Search config types..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        mb="md"
      />

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Prefix</Table.Th>
            <Table.Th>Symbol</Table.Th>
            <Table.Th>SK Template</Table.Th>
            <Table.Th>Filename Template</Table.Th>
            <Table.Th>Dims</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map((ct, i) => (
            <Table.Tr
              key={ct.filenamePrefix}
              style={{ cursor: 'pointer' }}
              onClick={() => handleRowClick(ct)}
            >
              <Table.Td><Text size="xs" c="dimmed">{i + 1}</Text></Table.Td>
              <Table.Td><Text size="sm" fw={500}>{ct.name}</Text></Table.Td>
              <Table.Td><Code>{ct.filenamePrefix}-</Code></Table.Td>
              <Table.Td><Badge variant="light" size="sm" ff="monospace">{ct.symbol}</Badge></Table.Td>
              <Table.Td><Code>{ct.skVerbose}</Code></Table.Td>
              <Table.Td>
                {ct.filenameTemplate ? (
                  <Code>{ct.filenameTemplate}</Code>
                ) : (
                  <Text size="xs" c="dimmed">—</Text>
                )}
              </Table.Td>
              <Table.Td>
                <Badge variant="dot" size="sm">{ct.dimensions.length}</Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={selected ? `Config Type: ${selected.name}` : 'Config Type'}
        position="right"
        size="lg"
      >
        {selected && (
          <Stack gap="md">
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Symbol</Text>
                <Badge size="lg" ff="monospace" variant="light">{selected.symbol}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Prefix</Text>
                <Code>{selected.filenamePrefix}-</Code>
              </div>
            </SimpleGrid>

            <Divider />

            <div>
              <Text size="sm" fw={600} mb="xs">Sort Key Templates</Text>
              <Text size="xs" c="dimmed">Verbose</Text>
              <Code block>{selected.skVerbose}</Code>
              <Text size="xs" c="dimmed" mt="xs">Compressed</Text>
              <Code block>{selected.skCompressed}</Code>
            </div>

            <Divider />

            <div>
              <Text size="sm" fw={600} mb="xs">Filename</Text>
              {selected.filenameTemplate && (
                <>
                  <Text size="xs" c="dimmed">Template (DRR-089)</Text>
                  <Code block>{selected.filenameTemplate}</Code>
                </>
              )}
              <Text size="xs" c="dimmed" mt="xs">ID Field</Text>
              <Code>{selected.idField}</Code>
            </div>

            <Divider />

            {selected.dimensions.length > 0 && (
              <div>
                <Text size="sm" fw={600} mb="xs">Dimensions ({selected.dimensions.length})</Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Maps To</Table.Th>
                      <Table.Th>Storage</Table.Th>
                      <Table.Th>Default</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {selected.dimensions.map((d) => (
                      <Table.Tr key={d.name}>
                        <Table.Td><Code>{d.name}</Code></Table.Td>
                        <Table.Td>{d.mapsTo ?? '—'}</Table.Td>
                        <Table.Td><Badge size="xs" variant="light">{d.storage ?? 'edge'}</Badge></Table.Td>
                        <Table.Td>{d.defaultValue ?? '—'}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}

            {selected.sourceDirs.length > 0 && (
              <div>
                <Text size="sm" fw={600} mb="xs">Source Directories</Text>
                <List size="sm">
                  {selected.sourceDirs.map((dir) => (
                    <List.Item key={dir}><Code>{dir}</Code></List.Item>
                  ))}
                </List>
              </div>
            )}

            {selected.seedLoader && (
              <div>
                <Text size="xs" c="dimmed">Seed Loader</Text>
                <Code>{selected.seedLoader}</Code>
              </div>
            )}

            {selected.legacySk && (
              <Badge color="yellow" variant="light" mt="sm">Legacy SK Pattern</Badge>
            )}
          </Stack>
        )}
      </Drawer>
    </>
  );
}

function DimensionTablesTab({ queryDoc, title }: { queryDoc: DocumentNode; title: string }) {
  const { data, loading, error } = useQuery<DimensionTablesData>(queryDoc);
  const [search, setSearch] = useState('');

  const tables: DimensionTable[] = useMemo(() => {
    const key = title === 'Filename Abbreviations' ? 'filenameAbbreviationTables' : 'dimensionCodeTables';
    return data?.[key] ?? [];
  }, [data, title]);

  const filtered = useMemo(() => {
    if (!search) return tables;
    const q = search.toLowerCase();
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.codes.some((c) => c.value.toLowerCase().includes(q))
    );
  }, [tables, search]);

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red" icon={<IconAlertTriangle size={16} />}>{error.message}</Alert>;

  return (
    <>
      <TextInput
        placeholder={`Search ${title.toLowerCase()}...`}
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        mb="md"
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {filtered.map((table) => (
          <Card key={table.name} withBorder p="sm">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>{table.name}</Text>
              <Badge size="sm" variant="light">{table.count} codes</Badge>
            </Group>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Value</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {table.codes.map((c) => (
                  <Table.Tr key={`${table.name}-${c.code}`}>
                    <Table.Td><Code>{c.code}</Code></Table.Td>
                    <Table.Td><Text size="sm">{c.value}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        ))}
      </SimpleGrid>
    </>
  );
}

function FilenameDecoderTab() {
  const [filename, setFilename] = useState('');
  const { data, loading } = useQuery<DecodeFilenameData>(DECODE_CONFIG_FILENAME, {
    variables: { filename },
    skip: filename.length < 3,
  });

  const decoded = data?.decodeConfigFilename;

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Paste a scope-encoded config filename to decode its dimensions (DRR-089).
      </Text>

      <TextInput
        placeholder="e.g., wf-2-0-001, rl-1-4-001, rn-0-0-001"
        leftSection={<IconFileCode size={16} />}
        value={filename}
        onChange={(e) => setFilename(e.currentTarget.value)}
        size="md"
      />

      {loading && <Loader size="sm" />}

      {decoded && (
        <Card withBorder p="md">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Code>{decoded.filename}</Code>
              {decoded.valid ? (
                <Badge color="green" variant="light">Valid</Badge>
              ) : (
                <Badge color="red" variant="light">Invalid</Badge>
              )}
            </Group>
            {decoded.symbol && (
              <Badge variant="light" ff="monospace" size="lg">{decoded.symbol}</Badge>
            )}
          </Group>

          {decoded.valid && decoded.configType && (
            <Stack gap="sm">
              <SimpleGrid cols={3}>
                <div>
                  <Text size="xs" c="dimmed">Config Type</Text>
                  <Text size="sm" fw={600}>{decoded.configType}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Prefix</Text>
                  <Code>{decoded.prefix}</Code>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Symbol</Text>
                  <Badge variant="light" ff="monospace">{decoded.symbol}</Badge>
                </div>
              </SimpleGrid>

              {decoded.segments.length > 0 && (
                <>
                  <Divider label="Decoded Dimensions" labelPosition="left" />
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Dimension</Table.Th>
                        <Table.Th>Raw Code</Table.Th>
                        <Table.Th>Decoded Value</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {decoded.segments.map((seg: { dimension: string; rawCode: string; decodedValue: string | null }) => (
                        <Table.Tr key={seg.dimension}>
                          <Table.Td><Code>{seg.dimension}</Code></Table.Td>
                          <Table.Td><Code>{seg.rawCode}</Code></Table.Td>
                          <Table.Td>
                            {seg.decodedValue ? (
                              <Tooltip label={`${seg.dimension}: ${seg.rawCode} → ${seg.decodedValue}`}>
                                <Badge variant="light" color="blue">{seg.decodedValue}</Badge>
                              </Tooltip>
                            ) : (
                              <Text size="sm" c="dimmed">{seg.rawCode === '?' ? 'missing' : seg.rawCode}</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}
            </Stack>
          )}

          {decoded.error && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mt="sm">
              {decoded.error}
            </Alert>
          )}
        </Card>
      )}
    </Stack>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function RegistryExplorerPage() {
  const { data: overviewData, loading, error } = useQuery<RegistryOverviewData>(GetRegistryOverviewDocument);

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Registry">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading config type registry...</Text>
        </Stack>
      </Container>
    );
  }

  const overview = overviewData?.registryOverview;

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Registry Explorer</Title>
          <Text size="sm" c="dimmed">
            Config type registry — 15+ types, dimension codes, filename decoder (DRR-111)
          </Text>
        </div>
        <Badge variant="light" color="violet" size="lg">Architect</Badge>
      </Group>

      {overview && <StatsCards overview={overview} />}

      <Tabs defaultValue="config-types">
        <Tabs.List mb="md">
          <Tabs.Tab value="config-types" leftSection={<IconListDetails size={16} />}>
            Config Types
          </Tabs.Tab>
          <Tabs.Tab value="dimensions" leftSection={<IconBinaryTree size={16} />}>
            Dimensions
          </Tabs.Tab>
          <Tabs.Tab value="abbreviations" leftSection={<IconCode size={16} />}>
            Filename Abbreviations
          </Tabs.Tab>
          <Tabs.Tab value="decoder" leftSection={<IconFileCode size={16} />}>
            Filename Decoder
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="config-types">
          <ConfigTypesTab />
        </Tabs.Panel>
        <Tabs.Panel value="dimensions">
          <DimensionTablesTab queryDoc={GetDimensionCodeTablesDocument} title="Dimensions" />
        </Tabs.Panel>
        <Tabs.Panel value="abbreviations">
          <DimensionTablesTab queryDoc={GetFilenameAbbreviationTablesDocument} title="Filename Abbreviations" />
        </Tabs.Panel>
        <Tabs.Panel value="decoder">
          <FilenameDecoderTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
